import { lazy, Suspense, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { AdmIcn } from './AdminIcons'
import { ShareDrawer } from '../ShareDrawer'
import { getDefaultImageForType } from '../ui/EventImagePicker'
import { useEvent } from '../../hooks/usePotlucks'
import { useChurch } from '../../hooks/useChurches'
import { useDuplicateEvent } from '../../hooks/useChurchAdmin'
import { buildPostContent } from '../../lib/socialCard'

const SocialPostModal = lazy(() => import('../SocialPostModal'))

export type EventAdminTab = 'overview' | 'guests' | 'edit' | 'day-of'

interface EventAdminTabsProps {
  slug: string
  eventId: string
  occurrenceId: string
  title: string
  startsAt?: string
  active: EventAdminTab
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  })
}

/**
 * Shared admin chrome for a single event. Ties the event-admin surfaces
 * (Overview · Guests · Edit · Day-of) together, mirrors the public page's
 * back-to-admin bar, and surfaces the universal actions (Share, Duplicate)
 * on every surface. Rendered at the top of each AdminShell event page.
 */
export function EventAdminTabs({ slug, eventId, occurrenceId, title, startsAt, active }: EventAdminTabsProps) {
  const navigate = useNavigate()
  const [shareOpen, setShareOpen] = useState(false)
  const [postOpen, setPostOpen] = useState(false)
  const { data: eventData } = useEvent(eventId)
  const { data: churchData } = useChurch(slug)
  const duplicate = useDuplicateEvent()

  const event = eventData?.data
  const status = event?.status
  const isDraft = status === 'draft'
  const liveUrl = typeof window !== 'undefined' ? `${window.location.origin}/events/${eventId}` : `/events/${eventId}`

  // "Create post" mirrors the public event page: anyone with admin access to this
  // event (this surface is gated to church owners/coordinators) can generate a
  // social card, except for scraped/unclaimed events which have no host to promote.
  const church = churchData?.data
  const postContent =
    event && event.source !== 'scraped'
      ? buildPostContent({
          event,
          occurrence: startsAt ? ({ startsAt } as Parameters<typeof buildPostContent>[0]['occurrence']) : null,
          hostName: church?.name ?? 'Fellowship',
          hostLogoUrl: church?.logoUrl ?? null,
          heroImageUrl: event.imageUrl ?? getDefaultImageForType(event.eventType ?? 'potluck', '900x900'),
          city: church?.city ?? null,
          denomination: church?.denomination ?? null,
          origin: typeof window !== 'undefined' ? window.location.origin : 'https://fellowship-app.com',
        })
      : null

  const tabs: { id: EventAdminTab; label: string; to: string }[] = [
    { id: 'overview', label: 'Overview', to: `/churches/${slug}/occurrences/${occurrenceId}/overview` },
    { id: 'guests', label: 'Guests', to: `/churches/${slug}/occurrences/${occurrenceId}/host` },
    { id: 'edit', label: 'Edit', to: `/churches/${slug}/events/${eventId}/edit` },
    { id: 'day-of', label: 'Day-of', to: `/churches/${slug}/occurrences/${occurrenceId}/day-of` },
  ]

  return (
    <div className="mb-6 -mt-1">
      {/* Back + actions row */}
      <div className="flex items-center justify-between gap-4 mb-3">
        <Link
          to={`/churches/${slug}/events`}
          className="inline-flex items-center gap-1.5 text-[12.5px] text-muted no-underline font-medium hover:text-ink-2"
        >
          <AdmIcn name="back" size={13} /> Events
        </Link>
        <div className="flex items-center gap-2.5 shrink-0">
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="h-[34px] px-3.5 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink border border-line bg-transparent cursor-pointer font-sans hover:border-ink-2 transition-colors"
          >
            <ShareIcon /> Share
          </button>
          <button
            type="button"
            onClick={() => duplicate.mutate(eventId, {
              onSuccess: (res) => navigate(`/churches/${slug}/occurrences/${res.data.occurrences[0].id}/overview`),
            })}
            disabled={duplicate.isPending}
            className="h-[34px] px-3.5 rounded-full inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink border border-line bg-transparent cursor-pointer font-sans hover:border-ink-2 transition-colors disabled:opacity-50"
          >
            <CopyIcon /> {duplicate.isPending ? 'Duplicating…' : 'Duplicate'}
          </button>
        </div>
      </div>

      {/* Event context */}
      <div className="flex items-center gap-3 flex-wrap mb-3.5">
        <h1 className="serif m-0 text-[30px] leading-[1.05] tracking-[-0.02em] font-medium min-w-0">
          {title}
        </h1>
        {status && (
          <span
            className="px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0"
            style={isDraft
              ? { background: 'var(--surface-2)', color: 'var(--muted)', border: '1px solid var(--line-soft)' }
              : { background: 'var(--primary-soft)', color: 'var(--primary-deep)' }}
          >
            {isDraft ? 'Draft' : 'Live'}
          </span>
        )}
      </div>
      {startsAt && (
        <div className="text-[13px] text-muted mb-3.5">{formatDate(startsAt)}</div>
      )}

      {/* Tab row */}
      <div className="flex items-center gap-1 border-b border-line-soft">
        {tabs.map((tab) => {
          const isActive = tab.id === active
          return (
            <Link
              key={tab.id}
              to={tab.to}
              aria-current={isActive ? 'page' : undefined}
              className={`px-3.5 py-2.5 text-[13.5px] no-underline -mb-px border-b-2 transition-colors ${
                isActive
                  ? 'border-primary text-primary font-semibold'
                  : 'border-transparent text-ink-2 font-medium hover:text-ink'
              }`}
            >
              {tab.label}
            </Link>
          )
        })}
        <div className="flex-1" />
        <a
          href={liveUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="px-3 py-2.5 text-[12.5px] text-muted no-underline font-medium inline-flex items-center gap-1.5 hover:text-ink-2"
        >
          View live <ExternalIcon />
        </a>
      </div>

      <ShareDrawer
        url={liveUrl}
        title={title}
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onCreatePost={postContent ? () => setPostOpen(true) : undefined}
      />
      {postContent && postOpen && (
        <Suspense fallback={null}>
          <SocialPostModal open={postOpen} onClose={() => setPostOpen(false)} content={postContent} />
        </Suspense>
      )}
    </div>
  )
}

// ─── Icons ──────────────────────────────────────────────────────────────────

function ShareIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </svg>
  )
}

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

function ExternalIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  )
}
